import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Bell, PhoneIncoming, PhoneOff, PhoneCall, Phone, PhoneForwarded, Users, Mic, MicOff, X } from 'lucide-react';
import { UserMenu } from '@/components/UserMenu';
import { useUserRole } from '@/hooks/useUserRole';
import { usePagePermissions } from '@/contexts/PagePermissionsContext';
import { useSipPhoneContext } from '@/contexts/SipPhoneContext';
import { formatDuration } from '@/pages/admin/phone/page';
import { supabase } from '@/lib/supabaseClient';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/ToastContext';
import { CallTargetPicker } from '@/components/feature/CallTargetPicker';
import { listTransferTargets, type TransferTarget } from '@/lib/sipApi';

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

const RECENT_NOTIFICATIONS_LIMIT = 6;

export interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
  badge?: number;
  end?: boolean;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

// Shared chrome for every module (BD/KR/Ops/Core/Executive/Admin): left icon+label
// sidebar, top bar with page title + notification bell + user menu, and a scrollable
// content area. One shell definition keeps every module visually identical instead
// of each page reinventing its own header/nav. User/role/unread-count are fetched
// here directly so every module gets a working profile menu, avatar, and sign-out
// for free instead of each page having to thread them through (previously none did —
// the old static avatar+chevron had no click handler and onLogout was never passed
// by any of the 21 callers, so Sign Out silently rendered nowhere).
export function AppShell({
  moduleTitle,
  moduleIcon,
  navGroups,
  children,
}: {
  moduleTitle: string;
  moduleIcon: ReactNode;
  navGroups: NavGroup[];
  children: ReactNode;
}) {
  const { user, role } = useUserRole();
  const { isFounder, allowedPages } = usePagePermissions();
  const navigate = useNavigate();
  const phone = useSipPhoneContext();
  const { push } = useToast();

  // Transfer and "add to conference" both need the same "pick a colleague's
  // extension" list — one bit of picker state covers both, distinguished by
  // pickerMode so the modal knows which action a pick should trigger.
  const [pickerMode, setPickerMode] = useState<'transfer' | 'conference' | null>(null);
  const [pickerTargets, setPickerTargets] = useState<TransferTarget[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerBusyId, setPickerBusyId] = useState<string | null>(null);

  const openPicker = async (mode: 'transfer' | 'conference') => {
    setPickerMode(mode);
    setPickerLoading(true);
    try {
      setPickerTargets(await listTransferTargets());
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setPickerLoading(false);
    }
  };

  const handlePick = async (t: TransferTarget) => {
    if (pickerMode === 'conference') {
      phone.startConference(t.extension, t.display_name);
      setPickerMode(null);
      return;
    }
    setPickerBusyId(t.user_id);
    try {
      await phone.transfer(t.extension, t.display_name);
      push('success', `Transferred to ${t.display_name} (Ext. ${t.extension})`);
      setPickerMode(null);
    } catch (e) {
      push('error', (e as Error).message);
    } finally {
      setPickerBusyId(null);
    }
  };

  // By the time AppShell mounts, RequirePagePermission (which wraps every
  // /admin/* route) has already waited for permissions to load — so this
  // never has to handle its own loading state, just filter.
  const visibleNavGroups = isFounder
    ? navGroups
    : navGroups
        .map((g) => ({ ...g, items: g.items.filter((i) => allowedPages.has(i.href)) }))
        .filter((g) => g.items.length > 0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<NotificationRow[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const loadNotifications = () => {
    if (!user) return;
    supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false)
      .then(({ count }) => setUnreadCount(count ?? 0));
    supabase
      .from('notifications')
      .select('id, type, title, message, read, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(RECENT_NOTIFICATIONS_LIMIT)
      .then(({ data }) => setRecentNotifications((data as NotificationRow[]) ?? []));
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Click-outside closes the dropdown, same pattern as the print-format
  // menu on the orders list.
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const openNotifications = () => {
    setNotifOpen((o) => {
      if (!o) loadNotifications(); // refresh right before showing, not just on mount
      return !o;
    });
  };

  const handleNotificationClick = async (n: NotificationRow) => {
    setNotifOpen(false);
    if (!n.read) {
      await supabase.from('notifications').update({ read: true }).eq('id', n.id);
      setUnreadCount((c) => Math.max(0, c - 1));
      setRecentNotifications((prev) => prev.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
    }
    navigate('/admin/notifications');
  };

  return (
    // print:h-auto + print:overflow-visible matter here specifically for
    // anything printed from inside AppShell (e.g. Delivery Challan's two
    // copies): h-screen pins this to the live browser window's height and
    // overflow-hidden hard-clips anything past it — a constraint tied to
    // viewport size, not physical page geometry, that Chromium's print
    // pipeline still honors. Found by generating an actual print PDF and
    // tracing the clip down through every ancestor: the inner scroll panel
    // (print:overflow-visible added separately, same reasoning) wasn't
    // enough on its own because THIS outer div was still capping total
    // printable height to one screenful, well short of what two full A4
    // pages need.
    <div className="admin-root h-screen print:h-auto flex bg-surface-subtle overflow-hidden print:overflow-visible">
      <aside className="w-60 flex-shrink-0 bg-white border-r border-border-subtle flex flex-col shadow-[1px_0_0_rgba(27,42,74,0.02),2px_0_16px_-4px_rgba(27,42,74,0.05)]">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border-subtle flex-shrink-0">
          <div className="w-8 h-8 flex items-center justify-center rounded-[10px] bg-gradient-to-br from-primary-700 to-primary-900 text-white shadow-pop flex-shrink-0">
            {moduleIcon}
          </div>
          <p className="text-[14px] font-bold text-ink tracking-tight truncate">{moduleTitle}</p>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-4 px-3 space-y-5">
          {visibleNavGroups.map((group, i) => (
            <div key={group.title ?? i}>
              {group.title && (
                <p className="px-2 mb-1.5 text-[10.5px] font-bold tracking-wider uppercase text-ink-disabled">
                  {group.title}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.end}
                    className={({ isActive }) =>
                      `relative flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-lg text-[13px] font-semibold transition-all duration-150 ${
                        isActive
                          ? 'bg-primary-50 text-primary-800'
                          : 'text-ink-muted hover:bg-surface-muted hover:text-ink'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-full bg-primary-800" />
                        )}
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">{item.icon}</span>
                        <span className="flex-1 truncate">{item.label}</span>
                        {!!item.badge && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-warning-bg text-warning">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 flex-shrink-0 flex items-center justify-between px-6 bg-white/80 backdrop-blur-sm border-b border-border-subtle sticky top-0 z-30">
          <div />
          <div className="flex items-center gap-3">
            {phone.hasCredentials && (
              <button
                type="button"
                onClick={phone.toggleDnd}
                className={`flex items-center gap-1.5 pl-2 pr-2.5 h-8 rounded-full text-[11.5px] font-semibold transition-colors cursor-pointer ${
                  phone.dnd
                    ? 'bg-error-bg text-error ring-1 ring-inset ring-error/20'
                    : 'bg-surface-muted text-ink-faint hover:text-ink'
                }`}
                title={phone.dnd ? 'Do Not Disturb is on — calls skip you and ring your teammates instead' : 'Do Not Disturb is off — you receive calls normally'}
              >
                {phone.dnd ? <PhoneOff className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                DND
              </button>
            )}
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={openNotifications}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-muted transition-colors duration-150 cursor-pointer"
                aria-label="Notifications"
              >
                <Bell className="w-[18px] h-[18px]" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-error ring-2 ring-white" />
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-border-subtle shadow-modal overflow-hidden z-50">
                  <div className="px-3.5 py-2.5 border-b border-border-subtle flex items-center justify-between">
                    <p className="text-[13px] font-bold text-ink">Notifications</p>
                    {unreadCount > 0 && <span className="text-[11px] font-semibold text-primary-800">{unreadCount} unread</span>}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {recentNotifications.length === 0 ? (
                      <p className="px-3.5 py-6 text-[12px] text-ink-faint text-center">No notifications yet</p>
                    ) : (
                      recentNotifications.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => handleNotificationClick(n)}
                          className={`w-full text-left px-3.5 py-2.5 border-b border-border-subtle last:border-0 hover:bg-surface-muted cursor-pointer ${
                            n.read ? '' : 'bg-primary-50/60'
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary-700 flex-shrink-0" />}
                            <p className="text-[12.5px] font-semibold text-ink truncate">{n.title}</p>
                          </div>
                          <p className="text-[11.5px] text-ink-faint truncate mt-0.5">{n.message}</p>
                          <p className="text-[10.5px] text-ink-disabled mt-1">{new Date(n.created_at).toLocaleString()}</p>
                        </button>
                      ))
                    )}
                  </div>
                  <NavLink
                    to="/admin/notifications"
                    onClick={() => setNotifOpen(false)}
                    className="block px-3.5 py-2.5 text-[12px] font-semibold text-primary-800 text-center hover:bg-surface-muted border-t border-border-subtle cursor-pointer"
                  >
                    View All →
                  </NavLink>
                </div>
              )}
            </div>
            {user && <UserMenu user={user} role={role} unreadCount={unreadCount} />}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto print:overflow-visible print:h-auto p-6">{children}</main>
      </div>

      {/* Incoming call — a call can ring in while staff are on any admin
          page, so this floats front-and-center over whatever they're
          looking at, the same way a phone's own incoming-call screen would. */}
      {phone.incomingCall && phone.callState === 'ringing' && phone.callDirection === 'inbound' && (
        <>
          <div className="fixed inset-0 z-40 bg-primary-900/20 backdrop-blur-[2px]" />
          <div className="fixed inset-x-0 top-6 z-50 flex justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-primary-200 bg-white shadow-card-hover ring-1 ring-black/5 animate-[popIn_0.18s_ease-premium]">
              <div className="flex items-center gap-4 p-4">
                <div className="w-12 h-12 rounded-full bg-primary-50 text-primary-800 flex items-center justify-center flex-shrink-0 ring-1 ring-inset ring-primary-100 animate-pulse">
                  <PhoneIncoming className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-ink">Incoming call</p>
                  <p className="text-[13px] text-ink-faint truncate">
                    {phone.callerInfo?.customer_name || phone.incomingCall.displayName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 px-4 pb-4">
                <button
                  type="button"
                  onClick={phone.reject}
                  className="flex-1 h-10 rounded-xl bg-error text-white font-semibold text-[13px] flex items-center justify-center gap-1.5 hover:opacity-90 cursor-pointer"
                >
                  <PhoneOff className="w-4 h-4" /> Reject
                </button>
                <button
                  type="button"
                  onClick={phone.answer}
                  className="flex-1 h-10 rounded-xl bg-success text-white font-semibold text-[13px] flex items-center justify-center gap-1.5 hover:opacity-90 cursor-pointer"
                >
                  <PhoneCall className="w-4 h-4" /> Answer
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Persistent in-call widget — a MicroSIP-style floating panel that
          stays put in one corner for as long as the call lasts, on every
          admin page, so staff never have to leave what they're doing (an
          order, a customer profile) just to mute, transfer, add someone to
          the call, or hang up. */}
      {(phone.callState === 'dialing' ||
        (phone.callState === 'ringing' && phone.callDirection === 'outbound') ||
        phone.callState === 'active') && (
        <div className="fixed bottom-5 right-5 z-40 w-80 rounded-2xl border border-border-subtle bg-white shadow-card-hover overflow-hidden animate-[popIn_0.18s_ease-premium]">
          <div className="flex items-center gap-3 p-3.5 bg-primary-50/60">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                phone.callState === 'active' ? 'bg-success-bg text-success' : 'bg-primary-100 text-primary-800 animate-pulse'
              }`}
            >
              <PhoneCall className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-bold text-ink truncate">
                {phone.callerInfo?.customer_name || phone.remoteNumber}
              </p>
              <p className="text-[11px] text-ink-faint tabular-nums">
                {phone.callState === 'dialing' && 'Dialing…'}
                {phone.callState === 'ringing' && 'Ringing…'}
                {phone.callState === 'active' && formatDuration(phone.callSeconds)}
              </p>
            </div>
          </div>

          {phone.confState !== 'idle' && (
            <div className="flex items-center gap-2 px-3.5 py-2 border-t border-border-subtle bg-surface-muted">
              <Users className="w-3.5 h-3.5 text-primary-800 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-ink truncate">{phone.confLabel || phone.confRemoteNumber}</p>
                <p className="text-[10.5px] text-ink-faint">
                  {phone.confState === 'dialing' && 'Adding…'}
                  {phone.confState === 'ringing' && 'Ringing…'}
                  {phone.confState === 'active' && 'In conference'}
                </p>
              </div>
              <button
                type="button"
                onClick={phone.endConferenceLeg}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-surface text-ink-faint hover:text-error cursor-pointer flex-shrink-0"
                aria-label="Remove from conference"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 p-3 border-t border-border-subtle">
            <button
              type="button"
              onClick={phone.toggleMute}
              disabled={phone.callState !== 'active'}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-muted text-ink-muted disabled:opacity-40 cursor-pointer flex-shrink-0"
              aria-label={phone.muted ? 'Unmute' : 'Mute'}
              title={phone.muted ? 'Unmute' : 'Mute'}
            >
              {phone.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => openPicker('transfer')}
              disabled={phone.callState !== 'active'}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-muted text-ink-muted disabled:opacity-40 cursor-pointer flex-shrink-0"
              aria-label="Transfer"
              title="Transfer"
            >
              <PhoneForwarded className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => openPicker('conference')}
              disabled={phone.callState !== 'active' || phone.confState !== 'idle'}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-muted text-ink-muted disabled:opacity-40 cursor-pointer flex-shrink-0"
              aria-label="Add to conference"
              title="Add to conference"
            >
              <Users className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={phone.hangup}
              className="flex-1 h-9 rounded-full bg-error text-white font-semibold text-[12.5px] flex items-center justify-center gap-1.5 hover:opacity-90 cursor-pointer"
            >
              <PhoneOff className="w-3.5 h-3.5" /> Hang Up
            </button>
          </div>
        </div>
      )}

      <Modal
        open={pickerMode !== null}
        onClose={() => setPickerMode(null)}
        title={pickerMode === 'conference' ? 'Add to Conference' : 'Transfer Call'}
        subtitle={pickerMode === 'conference' ? 'Pick who to add to this call' : 'Pick who to send this call to'}
        icon={pickerMode === 'conference' ? <Users className="w-3.5 h-3.5" /> : <PhoneForwarded className="w-3.5 h-3.5" />}
      >
        <CallTargetPicker
          targets={pickerTargets}
          loading={pickerLoading}
          busyId={pickerMode === 'transfer' ? pickerBusyId : null}
          onPick={handlePick}
        />
      </Modal>
    </div>
  );
}
